import { useEffect, useMemo, useState } from 'react'
import { skillService, type PendingSkillRequest, type SkillCatalogResult, type SkillMergePreview, type SkillRequestStatus, type WorkforceDevelopmentAnalytics, type CareerPath } from '../../../services/skills'
import { useI18n } from '../../../i18n/I18nContext'
import { EmptyState } from '../../ui'
import { SkillPicker } from '../../ui/SkillPicker'

type SkillsGovernancePanelProps = {
  canManageSkills: boolean
}

const statusOptions: Array<{ value: SkillRequestStatus; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'mapped', label: 'Mapped' },
  { value: 'rejected', label: 'Rejected' },
]

function confidenceLabel(skill: SkillCatalogResult) {
  return `${Math.round((skill.confidence || 0) * 100)}% ${skill.matchType}`
}

function impactValue(preview: SkillMergePreview | null, key: string) {
  return Number(preview?.impact?.[key] || 0)
}

export function SkillsGovernancePanel({ canManageSkills }: SkillsGovernancePanelProps) {
  const { t, formatDateTime } = useI18n()
  const [activeSection, setActiveSection] = useState<'requests' | 'merge' | 'workforce' | 'paths'>('requests')
  const [status, setStatus] = useState<SkillRequestStatus>('pending')
  const [requests, setRequests] = useState<PendingSkillRequest[]>([])
  const [loading, setLoading] = useState(false)
  const [savingId, setSavingId] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [mapTargets, setMapTargets] = useState<Record<string, SkillCatalogResult[]>>({})
  const [sourceSkills, setSourceSkills] = useState<SkillCatalogResult[]>([])
  const [targetSkills, setTargetSkills] = useState<SkillCatalogResult[]>([])
  const [preview, setPreview] = useState<SkillMergePreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [analytics, setAnalytics] = useState<WorkforceDevelopmentAnalytics | null>(null)
  const [analyticsLoading, setAnalyticsLoading] = useState(false)
  const [careerPaths, setCareerPaths] = useState<CareerPath[]>([])

  const sourceSkill = sourceSkills[0]
  const targetSkill = targetSkills[0]

  const loadRequests = () => {
    setLoading(true)
    setMessage(null)
    skillService.listSkillRequests(status)
      .then((data) => setRequests(data.requests || []))
      .catch(() => setMessage(t('Could not load requested skills.')))
      .finally(() => setLoading(false))
  }

  useEffect(loadRequests, [status])

  useEffect(() => {
    setPreview(null)
    if (!sourceSkill || !targetSkill || sourceSkill.id === targetSkill.id) return
    setPreviewLoading(true)
    skillService.previewSkillMerge(sourceSkill.id, targetSkill.id)
      .then((data) => setPreview(data.preview))
      .catch(() => setMessage(t('Could not load merge impact.')))
      .finally(() => setPreviewLoading(false))
  }, [sourceSkill?.id, targetSkill?.id])

  useEffect(() => {
    if (activeSection !== 'workforce') return
    setAnalyticsLoading(true)
    skillService.getWorkforceDevelopmentAnalytics()
      .then((data) => setAnalytics(data.analytics))
      .catch(() => setMessage(t('Could not load workforce development analytics.')))
      .finally(() => setAnalyticsLoading(false))
  }, [activeSection])

  useEffect(() => {
    if (activeSection !== 'paths') return
    skillService.listCareerPaths()
      .then((data) => setCareerPaths(data.careerPaths || []))
      .catch(() => setMessage(t('Could not load career paths.')))
  }, [activeSection])

  const pendingCount = useMemo(() => requests.filter((request) => request.status === 'pending').length, [requests])

  const resolveRequest = async (request: PendingSkillRequest, action: 'approve_new' | 'map_existing' | 'reject') => {
    const mappedSkillId = mapTargets[request.id]?.[0]?.id
    if (action === 'map_existing' && !mappedSkillId) {
      setMessage(t('Choose an existing skill before mapping the request.'))
      return
    }
    setSavingId(request.id)
    setMessage(null)
    try {
      await skillService.resolveSkillRequest(request.id, { action, mappedSkillId: action === 'map_existing' ? mappedSkillId : undefined })
      setMessage(action === 'approve_new' ? t('Skill request approved.') : action === 'map_existing' ? t('Skill request mapped.') : t('Skill request rejected.'))
      loadRequests()
    } catch {
      setMessage(t('Could not update the skill request.'))
    } finally {
      setSavingId(null)
    }
  }

  const confirmMerge = async () => {
    if (!sourceSkill || !targetSkill || sourceSkill.id === targetSkill.id) {
      setMessage(t('Choose different source and target skills.'))
      return
    }
    setSavingId('merge')
    setMessage(null)
    try {
      await skillService.mergeSkills(sourceSkill.id, targetSkill.id)
      setMessage(t('Skills merged and alias created.'))
      setSourceSkills([])
      setTargetSkills([])
      setPreview(null)
    } catch {
      setMessage(t('Could not merge skills.'))
    } finally {
      setSavingId(null)
    }
  }

  if (!canManageSkills) {
    return <EmptyState title={t('Skills unavailable')} description={t('Your current role does not include skill catalog management access.')} />
  }

  return (
    <section className="stack">
      <div className="admin-tabs" role="tablist" aria-label={t('Skill governance sections')}>
        <button type="button" className={`admin-tab ${activeSection === 'requests' ? 'active' : ''}`} onClick={() => setActiveSection('requests')}>{t('Requested Skills')}</button>
        <button type="button" className={`admin-tab ${activeSection === 'merge' ? 'active' : ''}`} onClick={() => setActiveSection('merge')}>{t('Merge Skills')}</button>
        <button type="button" className={`admin-tab ${activeSection === 'workforce' ? 'active' : ''}`} onClick={() => setActiveSection('workforce')}>{t('Workforce Development')}</button>
        <button type="button" className={`admin-tab ${activeSection === 'paths' ? 'active' : ''}`} onClick={() => setActiveSection('paths')}>{t('Career Paths')}</button>
      </div>

      {message ? <p className="helper">{message}</p> : null}

      {activeSection === 'requests' ? (
        <article className="panel workspace-panel stack">
          <div className="admin-panel-toolbar">
            <div>
              <h3>{t('Requested Skills')}</h3>
              <p className="helper">{t('{count} pending skill requests in this view.', { count: pendingCount })}</p>
            </div>
            <label>{t('Status')}
              <select className="input" value={status} onChange={(event) => setStatus(event.target.value as SkillRequestStatus)}>
                {statusOptions.map((option) => <option key={option.value} value={option.value}>{t(option.label)}</option>)}
              </select>
            </label>
          </div>
          <div className="table-wrap">
            <table className="admin-table">
              <thead><tr><th>{t('Requested Skill')}</th><th>{t('Requested By')}</th><th>{t('Context')}</th><th>{t('Created')}</th><th>{t('Suggested Matches')}</th><th>{t('Status')}</th><th>{t('Actions')}</th></tr></thead>
              <tbody>
                {requests.map((request) => (
                  <tr key={request.id}>
                    <td><strong>{request.requested_name}</strong></td>
                    <td>{request.requested_by_email || t('Unknown')}</td>
                    <td>{request.context || t('General')}</td>
                    <td>{formatDateTime(request.created_at)}</td>
                    <td>
                      <div className="admin-chip-list">
                        {(request.suggestions || []).slice(0, 3).map((skill) => (
                          <button type="button" className="admin-chip skill-suggestion-chip" key={skill.id} onClick={() => setMapTargets((current) => ({ ...current, [request.id]: [skill] }))}>
                            {skill.canonicalName} ({confidenceLabel(skill)})
                          </button>
                        ))}
                        {(request.suggestions || []).length === 0 ? <span className="helper">{t('No close matches')}</span> : null}
                      </div>
                      <SkillPicker
                        selectedSkills={mapTargets[request.id] || []}
                        onChange={(next) => setMapTargets((current) => ({ ...current, [request.id]: next.slice(-1) }))}
                        label={t('Map target')}
                        placeholder={t('Search existing skills')}
                        disabled={request.status !== 'pending'}
                      />
                    </td>
                    <td><span className="status-pill">{t(statusOptions.find((option) => option.value === request.status)?.label || request.status)}</span></td>
                    <td>
                      <div className="row-actions">
                        <button type="button" className="btn btn-secondary" disabled={request.status !== 'pending' || savingId === request.id} onClick={() => resolveRequest(request, 'approve_new')}>{t('Approve New')}</button>
                        <button type="button" className="btn btn-secondary" disabled={request.status !== 'pending' || savingId === request.id} onClick={() => resolveRequest(request, 'map_existing')}>{t('Map To Existing')}</button>
                        <button type="button" className="btn btn-ghost" disabled={request.status !== 'pending' || savingId === request.id} onClick={() => resolveRequest(request, 'reject')}>{t('Reject')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && requests.length === 0 ? <tr><td colSpan={7}><EmptyState title={t('No skill requests')} description={t('Requested skills for this status will appear here.')} /></td></tr> : null}
                {loading ? <tr><td colSpan={7}>{t('Loading requested skills...')}</td></tr> : null}
              </tbody>
            </table>
          </div>
        </article>
      ) : null}

      {activeSection === 'merge' ? (
        <article className="panel workspace-panel stack">
          <h3>{t('Merge Skills')}</h3>
          <div className="admin-form-grid">
            <SkillPicker selectedSkills={sourceSkills} onChange={(next) => setSourceSkills(next.slice(-1))} label={t('Source skill')} placeholder={t('Duplicate skill to deprecate')} />
            <SkillPicker selectedSkills={targetSkills} onChange={(next) => setTargetSkills(next.slice(-1))} label={t('Target skill')} placeholder={t('Canonical skill to keep')} />
          </div>
          {previewLoading ? <p className="helper">{t('Loading merge impact...')}</p> : null}
          {preview ? (
            <div className="admin-summary-grid">
              <div className="metric-card"><span>{t('Workers affected')}</span><strong>{impactValue(preview, 'workers_affected')}</strong></div>
              <div className="metric-card"><span>{t('Jobs affected')}</span><strong>{impactValue(preview, 'jobs_affected')}</strong></div>
              <div className="metric-card"><span>{t('Mentor claims affected')}</span><strong>{impactValue(preview, 'mentor_claims_affected')}</strong></div>
              <div className="metric-card"><span>{t('Learning goals affected')}</span><strong>{impactValue(preview, 'learning_goals_affected')}</strong></div>
              <div className="metric-card"><span>{t('Mentor rates affected')}</span><strong>{impactValue(preview, 'mentor_rates_affected')}</strong></div>
              <div className="metric-card"><span>{t('Aliases affected')}</span><strong>{impactValue(preview, 'aliases_affected')}</strong></div>
              <div className="metric-card"><span>{t('Relationships affected')}</span><strong>{impactValue(preview, 'relationships_affected')}</strong></div>
            </div>
          ) : null}
          <div className="admin-panel-toolbar">
            <p className="helper">{sourceSkill && targetSkill ? t('{source} will become an alias of {target}.', { source: sourceSkill.canonicalName, target: targetSkill.canonicalName }) : t('Choose source and target skills to preview impact.')}</p>
            <button type="button" className="btn btn-primary" disabled={!preview || savingId === 'merge'} onClick={confirmMerge}>{t('Confirm Merge')}</button>
          </div>
        </article>
      ) : null}

      {activeSection === 'workforce' ? (
        <article className="panel workspace-panel stack">
          <div className="admin-panel-toolbar">
            <div>
              <h3>{t('Workforce Development')}</h3>
              <p className="helper">{t('Skill graph analytics from jobs, actual work experience, mentorship goals, requests, and mentor coverage.')}</p>
            </div>
          </div>
          {analyticsLoading ? <p className="helper">{t('Loading workforce development analytics...')}</p> : null}
          {analytics ? (
            <div className="admin-summary-grid">
              <MetricList title={t('Top Missing Skills')} rows={analytics.topMissingSkills.map((row) => ({ label: row.canonicalName, value: row.count }))} emptyLabel={t('No signals yet')} />
              <MetricList title={t('Most Requested Skills')} rows={analytics.mostRequestedSkills.map((row) => ({ label: row.requestedName, value: row.count }))} emptyLabel={t('No signals yet')} />
              <MetricList title={t('Fastest Growing Skills')} rows={analytics.fastestGrowingSkills.map((row) => ({ label: row.canonicalName, value: row.count }))} emptyLabel={t('No signals yet')} />
              <MetricList title={t('Most Experienced Skills')} rows={(analytics.mostExperiencedSkills || []).map((row) => ({ label: row.canonicalName, value: Math.round(row.experienceHours) }))} emptyLabel={t('No signals yet')} />
              <MetricList title={t('Emerging Skills')} rows={(analytics.emergingSkills || []).map((row) => ({ label: row.canonicalName, value: row.workerCount }))} emptyLabel={t('No signals yet')} />
              <MetricList title={t('Skills With No Available Mentors')} rows={analytics.skillsWithNoAvailableMentors.map((row) => ({ label: row.canonicalName, value: row.activeGoalCount }))} emptyLabel={t('No signals yet')} />
              <MetricList title={t('Skill Shortages')} rows={(analytics.skillShortages || analytics.skillsBlockingJobFulfillment).map((row) => ({ label: row.canonicalName, value: row.openJobCount }))} emptyLabel={t('No signals yet')} />
              <MetricList title={t('Workers Near Promotion')} rows={(analytics.workersNearPromotion || []).map((row) => ({ label: `${row.workerName} / ${row.canonicalName}`, value: Math.round(row.nextLevelHours - row.experienceHours) }))} emptyLabel={t('No signals yet')} />
              <MetricList title={t('Potential Mentors')} rows={(analytics.potentialMentors || []).map((row) => ({ label: `${row.workerName} / ${row.canonicalName}`, value: Math.round(row.experienceHours) }))} emptyLabel={t('No signals yet')} />
            </div>
          ) : null}
        </article>
      ) : null}

      {activeSection === 'paths' ? (
        <article className="panel workspace-panel stack">
          <h3>{t('Career Paths')}</h3>
          <div className="table-wrap">
            <table className="admin-table">
              <thead><tr><th>{t('Path')}</th><th>{t('Steps')}</th><th>{t('Status')}</th></tr></thead>
              <tbody>
                {careerPaths.map((path) => (
                  <tr key={path.id}>
                    <td><strong>{path.name}</strong><br /><span className="helper">{path.description || t('No description')}</span></td>
                    <td>{path.steps.map((step) => step.title || step.targetSkillName).filter(Boolean).join(' -> ') || t('No steps')}</td>
                    <td><span className="status-pill">{t(path.status)}</span></td>
                  </tr>
                ))}
                {careerPaths.length === 0 ? <tr><td colSpan={3}><EmptyState title={t('No career paths')} description={t('Career path records will appear after they are seeded or added through admin tooling.')} /></td></tr> : null}
              </tbody>
            </table>
          </div>
        </article>
      ) : null}
    </section>
  )
}

function MetricList({ title, rows, emptyLabel }: { title: string; rows: Array<{ label: string; value: number }>; emptyLabel: string }) {
  return (
    <div className="metric-card stack" style={{ gap: '.5rem' }}>
      <strong>{title}</strong>
      {rows.length === 0 ? <span className="helper">{emptyLabel}</span> : rows.slice(0, 5).map((row) => (
        <span key={`${title}-${row.label}`} className="admin-panel-toolbar" style={{ padding: 0 }}>
          <span>{row.label}</span>
          <strong>{row.value}</strong>
        </span>
      ))}
    </div>
  )
}
